import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { CubeTypeENUM } from '@campaign-portal/namespace/common/rpc.params';
import {
	AlarisBalanceService,
	AlarisCheckboxComponent,
	AlarisIconDirective,
	AlarisLoadingDirective,
	AlarisLocalDateRangeTransformerDirective,
	AlarisProgressCircleComponent,
	ChartUtilsService,
	DashboardTimeFilterComponent,
	Day,
	FIRST_DAY,
	SharedTemplatesService,
} from '@campaign-portal/components-library';
import { TranslateModule } from '@ngx-translate/core';
import { TuiAxes, TuiBarChart, TuiLegendItem, TuiLineChart, TuiLineChartHint } from '@taiga-ui/addon-charts';
import { CurrencyPipe, NgTemplateOutlet } from '@angular/common';
import { TuiHint } from '@taiga-ui/core';
const SKELETON_BAR_MIN = 1000;
const SKELETON_BAR_MAX = 9000;
const SKELETON_BAR_COUNT = 12;
const SKELETON_BAR_MIN_COUNT = 2;

@Component({
	selector: 'app-chart-templates',
	templateUrl: './chart-templates.component.html',
	styleUrls: ['./chart-templates.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TranslateModule,
		DashboardTimeFilterComponent,
		AlarisLocalDateRangeTransformerDirective,
		TuiAxes,
		TuiBarChart,
		NgTemplateOutlet,
		TuiHint,
		TuiLegendItem,
		AlarisProgressCircleComponent,
		CurrencyPipe,
		AlarisIconDirective,
		AlarisCheckboxComponent,
		AlarisLoadingDirective,
		TuiLineChartHint,
		TuiLineChart,
	],
	standalone: true,
})
export class ChartTemplatesComponent implements AfterViewInit, OnDestroy {
	@ViewChild('period') readonly period!: TemplateRef<unknown>;
	@ViewChild('barChart') readonly barChart!: TemplateRef<unknown>;
	@ViewChild('barChartLegend') readonly barChartLegend!: TemplateRef<unknown>;
	@ViewChild('totalsProgressCharts') readonly totalsProgressCharts!: TemplateRef<unknown>;
	@ViewChild('totalStatistics') readonly totalStatistics!: TemplateRef<unknown>;
	@ViewChild('barChartSkeleton') readonly barChartSkeleton!: TemplateRef<unknown>;
	@ViewChild('chartVisibility') readonly chartVisibility!: TemplateRef<unknown>;
	@ViewChild('lineCharts') readonly lineCharts!: TemplateRef<unknown>;
	@ViewChild('lineChartSkeleton') readonly lineChartSkeleton!: TemplateRef<unknown>;

	public randomBarArray: number[][] = [];
	readonly CubeTypeENUM = CubeTypeENUM;
	readonly FIRST_DAY = FIRST_DAY;

	constructor(
		public readonly bs: AlarisBalanceService,
		private readonly sharedTemplates: SharedTemplatesService<unknown>,
		public readonly cu: ChartUtilsService
	) {
		this.sharedTemplates.loading$.next(true);
		this.randomBarArray = this.generateRandomBarArray(
			SKELETON_BAR_MIN_COUNT,
			SKELETON_BAR_COUNT,
			SKELETON_BAR_MIN,
			SKELETON_BAR_MAX
		);
	}

	get lastWeek(): Day {
		return Day.currentLocal().append({ day: -6 });
	}

	ngAfterViewInit(): void {
		this.sharedTemplates.share({
			period: this.period,
			barChart: this.barChart,
			barChartLegend: this.barChartLegend,
			totalsProgressCharts: this.totalsProgressCharts,
			totalStatistics: this.totalStatistics,
			barChartSkeleton: this.barChartSkeleton,
			chartVisibility: this.chartVisibility,
			lineCharts: this.lineCharts,
			lineChartSkeleton: this.lineChartSkeleton,
		});
		this.sharedTemplates.loading$.next(false);
	}

	ngOnDestroy(): void {
		this.sharedTemplates.clear();
	}

	generateRandomBarArray(rows: number, columns: number, min: number, max: number): number[][] {
		const result = [];
		for (let i = 0; i < rows; i++) {
			const row = [];
			for (let j = 0; j < columns; j++) {
				const randomValue = Math.floor(Math.random() * (max - min + 1)) + min;
				row.push(randomValue);
			}
			result.push(row);
		}
		return result;
	}
}
